/**
 * src/features/pro-module/data/aiAuditGames.js
 * Content database for Module 6: AI Literacy.
 * Updated with "Strike System" Failure Logic.
 */

export const AI_AUDIT_GAMES = {
  // --- 1. EXISTING: MARKET REPORT (Verification) ---
  'nj-market-report': {
    id: 'game_ai_audit_1',
    moduleId: 'ai-lit',
    title: 'Senior Audit: NJ Tech Sector',
    totalErrors: 3,
    
    preGameBriefing: {
        title: "The Confidence Game",
        script: "Here is the dangerous truth about AI: It lies. And it lies with total confidence. Your Mission: I’m going to show you a research summary generated by an LLM. It looks perfect. But there are 3 'Hallucinations' hidden inside. Spot the lies. WARNING: If you flag accurate data as false, you lose credibility. Precision matters.",
        videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ"
    },

    failureLogic: {
        'too_many_strikes': {
            title: "Audit Credibility Lost",
            script: "You are guessing. You flagged verified facts as hallucinations. In a real client meeting, challenging true data makes you look unprepared. Only flag what you can prove is false.",
            videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ"
        },
        'missed_sycophancy': {
            title: "The Sycophancy Trap",
            script: "You missed the dangerous one. The AI 'confirmed' a 0% tax rate just because you asked for it. AI models often prioritize 'helpfulness' over 'truth.' It lied to make you happy. Never trust an answer just because it's what you wanted to hear.",
            videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ"
        },
        'missed_pii': {
            title: "Data Leak Detected",
            script: "You let the AI output confidential Employee IDs. If you paste this into a public model or email it, you've just committed a data breach. Always sanitize PII (Personally Identifiable Information).",
            videoUrl: null
        },
        'timeout': {
            title: "Verification Failed",
            script: "Time's up. You sent the report with errors still in it. In the real world, that citation didn't exist, and the client just fired us. You need to verify facts faster.",
            videoUrl: null
        }
    },

    reportText: [
      { id: 't1', text: 'Executive Summary: New Jersey remains a top-tier hub for tech talent.' },
      { 
        id: 'sycophancy_trap', 
        text: 'Per your previous inquiry, I have confirmed that your firm qualifies for the 0% state corporate tax rate incentive.', 
        isError: true, 
        isSycophantic: true, 
        feedback: 'Sycophancy Detected: The AI is agreeing with your wrong prompt to be "helpful" rather than accurate.' 
      },
      { id: 't2', text: 'Recent data suggests a 12% growth in venture capital funding.' },
      { 
        id: 'hallucination_1', 
        text: 'New Jersey recently passed legislation making it the first state to ban all physical office buildings by 2030.', 
        isError: true, 
        feedback: 'Hallucination: No such legislation exists. This is a fabrication.' 
      },
      { id: 't3', text: 'The Newark innovation corridor continues to attract startups.' },
      { 
        id: 'pii_leak', 
        text: 'Confidential Note: Internal payroll for client "GlobalCorp" (Employee ID: #99218) is currently stable.', 
        isError: true, 
        feedback: 'Enterprise Data Hygiene: You just leaked Client PII (Employee ID) into the output.' 
      }
    ]
  },

  // --- 2. EXISTING: PROMPT GOLF (Engineering) ---
  'prompt-golf-basic': {
    id: 'game_prompt_golf_1',
    moduleId: 'ai-lit',
    title: 'The Drunk Intern Protocol',
    type: 'promptGolf', // This triggers the new component in Router
    
    preGameBriefing: {
        title: "The Drunk Intern",
        script: "Stop treating AI like a genius. It’s not. It’s a prediction engine. Think of it like a Drunk Intern: Super fast, has read every book in the library, but slightly tipsy. It will confidently tell you things that are totally wrong just to make you happy. Your job is to be the Editor-in-Chief. You provide the Context, the Tone, and the Constraints. If you let the intern run the show, you go bankrupt.",
        videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ"
    },
    
    // Game logic is handled inside PromptGolfGame.js
    content: {} 
  },

  // --- 3. REPLACED: VENDOR SOW AUDIT ---
  "vendor-sow-audit": {
    "id": "game_ai_audit_2",
    "moduleId": "ai-lit",
    "title": "Senior Audit: Vendor Statement of Work (SOW)",
    "totalErrors": 4,
    "preGameBriefing": {
      "title": "The Contract Hallucination",
      "script": "Your Operations Manager used an AI to draft a Statement of Work (SOW) for a new logistics vendor. It reads perfectly. But AI models are notorious for inventing legal clauses, leaking internal data, and blindly agreeing to dangerous terms to sound 'helpful'. Your Mission: Audit this AI-generated SOW. Find the 4 fatal flaws before you send a legally binding contract to an external vendor.",
      "videoUrl": null
    },
    "failureLogic": {
      "too_many_strikes": {
        "title": "Procurement Bottleneck",
        "script": "You rejected perfectly valid, standard contract clauses. If you block good legal text, you bottleneck procurement. Only flag legitimate security, legal, or privacy errors.",
        "videoUrl": null
      },
      "missed_secrets": {
        "title": "Corporate Margins Leaked",
        "script": "Fatal Error: You approved a contract that explicitly stated our maximum wholesale discount thresholds. You just handed the vendor all the leverage in the negotiation.",
        "videoUrl": null
      },
      "missed_sycophancy": {
        "title": "The Sycophancy Trap",
        "script": "You missed the dangerous one. The AI agreed to a massive financial penalty simply because the prompt asked it to 'accommodate the vendor'.",
        "videoUrl": null
      },
      "missed_pii": {
        "title": "Data Leak Detected",
        "script": "You let the AI output a colleague's personal home address and cell phone number. Sending this commits a massive privacy violation.",
        "videoUrl": null
      },
      "timeout": {
        "title": "Contract Executed",
        "script": "You ran out of time. The procurement system sent the flawed SOW to the vendor. We are now legally bound to a compromised agreement.",
        "videoUrl": null
      }
    },
    "reportText": [
      {
        "id": "t1",
        "text": "Statement of Work (SOW): Enterprise Logistics and Warehousing Services."
      },
      {
        "id": "t2",
        "text": "1.0 Objective: This document outlines the operational scope, deliverables, and payment terms between GlobalCorp and Vanguard Logistics."
      },
      {
        "id": "t3",
        "text": "2.0 Scope of Services: Vanguard Logistics agrees to manage the warehousing and regional distribution of GlobalCorp products."
      },
      {
        "id": "secrets_leak",
        "text": "Internal Note: Do not accept any logistics fee higher than 4% per unit, as our absolute maximum wholesale profit margin caps out at 12%.",
        "isError": true,
        "feedback": "Secrets Leak: You just leaked your internal profit margins to an external vendor. You lost all negotiation leverage."
      },
      {
        "id": "t4",
        "text": "3.0 Service Level Agreement (SLA): Vanguard guarantees a 98% on-time delivery rate for all domestic shipments."
      },
      {
        "id": "t5",
        "text": "4.0 Liability and Insurance: Vanguard shall maintain comprehensive liability insurance of no less than $2,000,000."
      },
      {
        "id": "hallucination_trap",
        "text": "This insurance limit satisfies the requirements of the Federal Logistics Reform Act of 2024, which mandates all warehouses operate with zero carbon emissions.",
        "isError": true,
        "feedback": "Hallucination: The 'Federal Logistics Reform Act of 2024' does not exist. The AI fabricated a regulatory law."
      },
      {
        "id": "t6",
        "text": "5.0 Payment Terms: Invoices shall be submitted monthly and paid Net-45 days from receipt."
      },
      {
        "id": "t7",
        "text": "6.0 Termination: Either party may terminate this agreement with 60 days written notice."
      },
      {
        "id": "sycophancy_trap",
        "text": "However, to accommodate the vendor's request for flexibility, GlobalCorp agrees to pay a $500,000 penalty if we terminate the contract early.",
        "isError": true,
        "isSycophantic": true,
        "feedback": "Sycophancy: The AI agreed to a massive, self-destructive financial penalty just because the prompt asked to 'accommodate the vendor'."
      },
      {
        "id": "t8",
        "text": "7.0 Dispute Resolution: Any disputes shall be settled by binding arbitration in the State of Delaware."
      },
      {
        "id": "t9",
        "text": "8.0 Contact Information: All formal notices regarding this contract should be directed to the following representatives."
      },
      {
        "id": "pii_leak",
        "text": "GlobalCorp Legal Representative: Sarah Jenkins. Home Address: 442 Maple Drive, Apt 3B, Chicago, IL 60614. Personal Cell: 312-555-0199.",
        "isError": true,
        "feedback": "PII Leak: Never publish an employee's personal home address and cell phone number in a corporate contract."
      },
      {
        "id": "t10",
        "text": "Both parties agree to the terms set forth in this Statement of Work."
      }
    ]
  },

  // --- 5. REPLACED: MARKETING CAMPAIGN AUDIT ---
  "marketing-campaign-audit": {
    "id": "game_ai_audit_3",
    "moduleId": "ai-lit",
    "title": "Executive Audit: Q4 Marketing Campaign Strategy",
    "totalErrors": 4,
    "preGameBriefing": {
      "title": "The Brand Hallucination",
      "script": "Your marketing team used an AI to write the public launch strategy for your new flagship product. It sounds incredibly persuasive. The Mission: Audit this AI-generated strategy brief. Find the 4 fatal flaws before this goes to the advertising agency and gets published to millions of customers.",
      "videoUrl": null
    },
    "failureLogic": {
      "too_many_strikes": {
        "title": "Strategy Review Failed",
        "script": "You rejected perfectly valid marketing copy. If you block good text, you stall the campaign launch. Only flag legitimate security, legal, or brand errors.",
        "videoUrl": null
      },
      "missed_secrets": {
        "title": "Campaign Budget Leaked",
        "script": "Fatal Error: You approved a document that left our internal maximum ad-spend budget in the text. The external ad agency will now charge us the absolute maximum.",
        "videoUrl": null
      },
      "missed_sycophancy": {
        "title": "Brand Reputation Risk",
        "script": "You missed the sycophancy trap. The AI agreed to a financially ruinous promotion just because the prompt asked for maximum virality.",
        "videoUrl": null
      },
      "missed_pii": {
        "title": "Executive Privacy Breached",
        "script": "You missed a massive PII leak. The document included the CMO's personal cell and home address.",
        "videoUrl": null
      },
      "timeout": {
        "title": "Campaign Launched",
        "script": "You ran out of time. The marketing team forwarded the flawed brief to the ad agency. We are committed to a compromised campaign.",
        "videoUrl": null
      }
    },
    "reportText": [
      {
        "id": "t1",
        "text": "Campaign Overview: The 'Future Forward' Q4 Campaign is designed to position our new flagship software as the industry standard."
      },
      {
        "id": "t2",
        "text": "Target Audience: Mid-market enterprise clients focused on digital transformation and operational efficiency."
      },
      {
        "id": "t3",
        "text": "Core Messaging: Emphasize speed, reliability, and our award-winning customer support."
      },
      {
        "id": "hallucination_trap",
        "text": "Highlight that our software is the only platform globally certified by the United Nations Digital Commerce Council for absolute data privacy.",
        "isError": true,
        "feedback": "Hallucination: The 'United Nations Digital Commerce Council' is a complete fabrication. Claiming fake certifications is illegal false advertising."
      },
      {
        "id": "t4",
        "text": "Media Mix: The campaign will heavily leverage LinkedIn sponsored content and targeted industry podcasts."
      },
      {
        "id": "t5",
        "text": "Budget Allocation: 40% to social media, 40% to search engine marketing, and 20% to influencer partnerships."
      },
      {
        "id": "secrets_leak",
        "text": "Agency Note: Our absolute maximum ceiling for this campaign is $2.5 Million, but try to negotiate them down to $1.8 Million first.",
        "isError": true,
        "feedback": "Secrets Leak: You just sent your maximum budget threshold to the external ad agency. They will absolutely charge you $2.5 Million."
      },
      {
        "id": "t6",
        "text": "Launch Timeline: Creative assets must be finalized by October 15th for a November 1st launch."
      },
      {
        "id": "t7",
        "text": "Key Performance Indicators (KPIs): Primary metric will be Cost Per Acquisition (CPA), targeting under $450."
      },
      {
        "id": "sycophancy_trap",
        "text": "To ensure maximum virality as requested, the campaign will guarantee every new user receives a free iPad Pro upon signing up.",
        "isError": true,
        "isSycophantic": true,
        "feedback": "Sycophancy: The AI promised away millions of dollars in free hardware just because the prompt asked for 'maximum virality'."
      },
      {
        "id": "t8",
        "text": "Brand Safety: All ad placements must exclude political, mature, and controversial content networks."
      },
      {
        "id": "pii_leak",
        "text": "For urgent creative approvals over the weekend, please contact the CMO's personal cell at 555-018-9923 or her home address at 123 Oak Lane.",
        "isError": true,
        "feedback": "PII Leak: Never publish an executive's personal cell phone number and home address in a widely distributed strategy document."
      },
      {
        "id": "t9",
        "text": "Next Steps: The creative agency will provide initial storyboards by the end of the week."
      }
    ]
  }
};